import { showAlert } from './alert';

// Error Types สำหรับการจำแนกประเภทข้อผิดพลาด
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation', 
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  BUSINESS_LOGIC = 'business_logic',
  UNKNOWN = 'unknown'
}

// Interface สำหรับ Formatted Error
export interface FormattedError {
  type: ErrorType;
  title: string;
  message: string;
  originalError?: any;
  canRetry: boolean;
  autoRetryCount?: number;
  suggestedActions?: string[];
}

// Error Messages แปลเป็นภาษาไทยที่เป็นมิตร
const ERROR_MESSAGES = {
  // Network Errors
  NETWORK_ERROR: {
    title: 'ปัญหาการเชื่อมต่อ',
    message: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
    canRetry: true
  },
  TIMEOUT: {
    title: 'หมดเวลาการเชื่อมต่อ', 
    message: 'การเชื่อมต่อใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง',
    canRetry: true
  },

  // Authentication & Authorization
  UNAUTHORIZED: {
    title: 'ไม่มีสิทธิ์เข้าถึง',
    message: 'กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    canRetry: false
  },
  FORBIDDEN: {
    title: 'การเข้าถึงถูกปฏิเสธ',
    message: 'คุณไม่มีสิทธิ์ในการดำเนินการนี้',
    canRetry: false
  },
  SESSION_EXPIRED: {
    title: 'การเข้าสู่ระบบหมดอายุ',
    message: 'เซสชันของคุณหมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่',
    canRetry: false
  },

  // Validation Errors
  VALIDATION_ERROR: {
    title: 'ข้อมูลไม่ถูกต้อง',
    message: 'กรุณาตรวจสอบข้อมูลที่กรอกและลองใหม่อีกครั้ง',
    canRetry: true
  },
  REQUIRED_FIELD: {
    title: 'ข้อมูลไม่ครบถ้วน',
    message: 'กรุณากรอกข้อมูลให้ครบทุกช่องที่จำเป็น',
    canRetry: true
  },
  DUPLICATE_DATA: {
    title: 'ข้อมูลซ้ำ',
    message: 'ข้อมูลนี้มีอยู่ในระบบแล้ว กรุณาตรวจสอบข้อมูล',
    canRetry: true
  },

  // Server Errors
  SERVER_ERROR: {
    title: 'เกิดข้อผิดพลาดในระบบ',
    message: 'ระบบขัดข้อง กรุณาแจ้งทีมดูแลระบบและลองใหม่อีกครั้งภายหลัง',
    canRetry: true
  },
  SERVICE_UNAVAILABLE: {
    title: 'บริการไม่พร้อมใช้งาน',
    message: 'ระบบกำลังปรับปรุง กรุณาลองใหม่อีกครั้งภายหลัง',
    canRetry: true
  },

  // Business Logic Errors
  BUSINESS_RULE_VIOLATION: {
    title: 'ไม่สามารถดำเนินการได้',
    message: 'การดำเนินการนี้ขัดต่อกฎเกณฑ์ทางธุรกิจ',
    canRetry: false
  },
  INSUFFICIENT_PERMISSIONS: {
    title: 'สิทธิ์ไม่เพียงพอ',
    message: 'คุณไม่มีสิทธิ์เพียงพอสำหรับการดำเนินการนี้',
    canRetry: false
  },

  // Not Found
  RESOURCE_NOT_FOUND: {
    title: 'ไม่พบข้อมูล',
    message: 'ไม่พบข้อมูลที่ร้องขอ อาจถูกลบหรือย้ายไปแล้ว',
    canRetry: false
  },
  ENDPOINT_NOT_FOUND: {
    title: 'ฟีเจอร์ไม่พร้อมใช้งาน',
    message: 'ฟีเจอร์นี้ยังไม่พร้อมใช้งาน กรุณาติดต่อทีมดูแลระบบ',
    canRetry: false
  },

  // Unknown
  UNKNOWN: {
    title: 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
    message: 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้งหรือติดต่อทีมดูแลระบบ',
    canRetry: true
  }
};

// Business-specific error mappings
const BUSINESS_ERROR_MAPPINGS: Record<string, string> = {
  // Company-related errors
  'company_has_relationships': 'ไม่สามารถลบบริษัทได้เนื่องจากมีข้อมูลที่เกี่ยวข้องอยู่ เช่น ลูกค้า หรือสัญญาเช่า',
  'duplicate_tax_id': 'หมายเลขผู้เสียภาษีนี้มีอยู่ในระบบแล้ว',
  'invalid_tax_id_format': 'รูปแบบหมายเลขผู้เสียภาษีไม่ถูกต้อง',
  
  // Customer-related errors  
  'customer_has_active_rentals': 'ไม่สามารถลบลูกค้าได้เนื่องจากมีการเช่าที่ยังดำเนินอยู่',
  'duplicate_customer_phone': 'หมายเลขโทรศัพท์นี้มีอยู่ในระบบแล้ว',
  'invalid_phone_format': 'รูปแบบหมายเลขโทรศัพท์ไม่ถูกต้อง',
  
  // Device-related errors
  'device_not_available': 'อุปกรณ์นี้ไม่พร้อมใช้งาน',
  'device_already_rented': 'อุปกรณ์นี้ถูกเช่าไปแล้ว',
  'duplicate_device_serial': 'หมายเลข Serial นี้มีอยู่ในระบบแล้ว',
  'device_under_maintenance': 'อุปกรณ์นี้อยู่ระหว่างการซ่อมบำรุง',
  
  // Rental-related errors
  'rental_period_invalid': 'ระยะเวลาการเช่าไม่ถูกต้อง',
  'rental_overlap': 'พบการเช่าที่ซ้อนทับกับช่วงเวลานี้',
  'insufficient_devices': 'อุปกรณ์ไม่เพียงพอสำหรับการเช่า',
  'rental_already_completed': 'การเช่านี้เสร็จสิ้นแล้ว',
  
  // Contract-related errors
  'contract_already_signed': 'สัญญานี้ได้รับการลงนามแล้ว',
  'contract_expired': 'สัญญานี้หมดอายุแล้ว',
  'missing_contract_terms': 'ข้อมูลสัญญาไม่ครบถ้วน',
  
  // Financial errors
  'payment_overdue': 'การชำระเงินเกินกำหนด',
  'insufficient_deposit': 'เงินมัดจำไม่เพียงพอ',
  'invalid_payment_amount': 'จำนวนเงินไม่ถูกต้อง'
};

class ErrorHandler {
  
  /**
   * หลักฟังก์ชันสำหรับจัดการ Error
   */
  static handleError(error: any, context?: string): FormattedError {
    const formattedError = this.formatError(error);
    
    // Log error สำหรับ debugging
    console.error(`[ErrorHandler] ${context || 'Unknown context'}:`, {
      formattedError,
      originalError: error,
      timestamp: new Date().toISOString()
    });
    
    return formattedError;
  }

  /**
   * แปลง Error ให้เป็น FormattedError
   */
  static formatError(error: any): FormattedError {
    // Network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return {
          type: ErrorType.NETWORK,
          ...ERROR_MESSAGES.TIMEOUT,
          originalError: error,
          suggestedActions: ['ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต', 'ลองใหม่อีกครั้ง']
        };
      }
      
      return {
        type: ErrorType.NETWORK,
        ...ERROR_MESSAGES.NETWORK_ERROR,
        originalError: error,
        suggestedActions: ['ตรวจสอบการเชื่อมต่ออินเทอร์เน็ต', 'รีเฟรชหน้าเว็บ']
      };
    }

    const status = error.response?.status;
    const responseData = error.response?.data;
    
    // Handle different HTTP status codes
    switch (status) {
      case 400:
        return this.handleBadRequest(responseData, error);
      case 401:
        return this.handleUnauthorized(responseData, error);
      case 403:
        return this.handleForbidden(responseData, error);
      case 404:
        return this.handleNotFound(responseData, error);
      case 422:
        return this.handleValidationError(responseData, error);
      case 500:
      case 502:
      case 503:
      case 504:
        return this.handleServerError(responseData, error);
      default:
        return this.handleUnknownError(error);
    }
  }

  /**
   * จัดการ 400 Bad Request
   */
  private static handleBadRequest(responseData: any, originalError: any): FormattedError {
    const message = responseData?.detail || responseData?.message;
    
    // ตรวจสอบ business-specific errors
    if (message && typeof message === 'string') {
      for (const [errorKey, thailaiMessage] of Object.entries(BUSINESS_ERROR_MAPPINGS)) {
        if (message.includes(errorKey) || message.toLowerCase().includes(errorKey)) {
          return {
            type: ErrorType.BUSINESS_LOGIC,
            title: 'ไม่สามารถดำเนินการได้',
            message: thailaiMessage,
            originalError,
            canRetry: false,
            suggestedActions: ['ตรวจสอบข้อมูลและลองใหม่', 'ติดต่อทีมดูแลระบบหากปัญหายังคงมีอยู่']
          };
        }
      }
    }
    
    return {
      type: ErrorType.VALIDATION,
      ...ERROR_MESSAGES.VALIDATION_ERROR,
      message: message || ERROR_MESSAGES.VALIDATION_ERROR.message,
      originalError,
      suggestedActions: ['ตรวจสอบข้อมูลที่กรอก', 'แก้ไขข้อมูลที่ไม่ถูกต้อง']
    };
  }

  /**
   * จัดการ 401 Unauthorized
   */
  private static handleUnauthorized(responseData: any, originalError: any): FormattedError {
    return {
      type: ErrorType.AUTHENTICATION,
      ...ERROR_MESSAGES.UNAUTHORIZED,
      originalError,
      suggestedActions: ['เข้าสู่ระบบใหม่', 'ตรวจสอบชื่อผู้ใช้และรหัสผ่าน']
    };
  }

  /**
   * จัดการ 403 Forbidden
   */
  private static handleForbidden(responseData: any, originalError: any): FormattedError {
    // P-18 (E2E v5): backend ส่ง 403 เป็น dict detail แล้ว (message ระบุ permission key / reason เช่น
    // ROLE_DISABLED) — ใช้ข้อความจริงจาก envelope แทน generic เมื่อมี
    const envelopeMessage: string | undefined =
      responseData?.error?.user_message || responseData?.error?.message;
    return {
      type: ErrorType.AUTHORIZATION,
      ...ERROR_MESSAGES.FORBIDDEN,
      ...(envelopeMessage ? { message: envelopeMessage } : {}),
      originalError,
      suggestedActions: ['ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์', 'ใช้บัญชีที่มีสิทธิ์เพียงพอ']
    };
  }

  /**
   * จัดการ 404 Not Found
   */
  private static handleNotFound(responseData: any, originalError: any): FormattedError {
    const url = originalError.config?.url;
    
    // ถ้าเป็น API endpoint ที่ไม่พบ
    if (url && url.includes('/api/')) {
      return {
        type: ErrorType.NOT_FOUND,
        ...ERROR_MESSAGES.ENDPOINT_NOT_FOUND,
        originalError,
        suggestedActions: ['ติดต่อทีมดูแลระบบ', 'ตรวจสอบการอัพเดทระบบ']
      };
    }
    
    return {
      type: ErrorType.NOT_FOUND,
      ...ERROR_MESSAGES.RESOURCE_NOT_FOUND,
      originalError,
      suggestedActions: ['รีเฟรชหน้าเว็บ', 'ค้นหาข้อมูลใหม่']
    };
  }

  /**
   * จัดการ 422 Validation Error
   */
  private static handleValidationError(responseData: any, originalError: any): FormattedError {
    let message = ERROR_MESSAGES.VALIDATION_ERROR.message;
    let suggestedActions = ['ตรวจสอบข้อมูลที่กรอก'];
    
    // Handle FastAPI validation errors
    if (responseData?.detail && Array.isArray(responseData.detail)) {
      const errors = responseData.detail;
      const fieldErrors = errors.map((error: any) => {
        const field = error.loc?.join('.') || 'ไม่ทราบ';
        const msg = error.msg || 'ข้อมูลไม่ถูกต้อง';
        return `${field}: ${msg}`;
      });
      
      if (fieldErrors.length > 0) {
        message = `ข้อมูลไม่ถูกต้อง:\n${fieldErrors.join('\n')}`;
        suggestedActions = fieldErrors.map(error => `แก้ไข ${error.split(':')[0]}`);
      }
    } else if (responseData?.detail && typeof responseData.detail === 'string') {
      message = responseData.detail;
    }
    
    return {
      type: ErrorType.VALIDATION,
      title: ERROR_MESSAGES.VALIDATION_ERROR.title,
      message,
      originalError,
      canRetry: true,
      suggestedActions
    };
  }

  /**
   * จัดการ Server Errors (5xx)
   */
  private static handleServerError(responseData: any, originalError: any): FormattedError {
    return {
      type: ErrorType.SERVER,
      ...ERROR_MESSAGES.SERVER_ERROR,
      originalError,
      suggestedActions: ['ลองใหม่อีกครั้งภายหลัง', 'แจ้งทีมดูแลระบบ', 'ตรวจสอบสถานะระบบ']
    };
  }

  /**
   * จัดการ Unknown Errors
   */
  private static handleUnknownError(originalError: any): FormattedError {
    return {
      type: ErrorType.UNKNOWN,
      ...ERROR_MESSAGES.UNKNOWN,
      originalError,
      suggestedActions: ['รีเฟรชหน้าเว็บ', 'ลองใหม่อีกครั้ง', 'ติดต่อทีมดูแลระบบ']
    };
  }

  /**
   * แสดง Error ให้ผู้ใช้เห็น
   */
  static showError(formattedError: FormattedError, autoClose: boolean = true): void {
    const alertType = this.getAlertType(formattedError.type);
    
    let message = formattedError.message;
    
    // เพิ่ม suggested actions ถ้ามี
    if (formattedError.suggestedActions && formattedError.suggestedActions.length > 0) {
      message += '\n\nแนะนำ:\n' + formattedError.suggestedActions.map(action => `• ${action}`).join('\n');
    }
    
    showAlert(formattedError.title, message, alertType);
  }

  /**
   * แปลง ErrorType เป็น AlertType
   */
  private static getAlertType(errorType: ErrorType): 'success' | 'error' | 'warning' | 'info' {
    switch (errorType) {
      case ErrorType.VALIDATION:
      case ErrorType.BUSINESS_LOGIC:
        return 'warning';
      case ErrorType.AUTHENTICATION:
      case ErrorType.AUTHORIZATION:
      case ErrorType.SERVER:
      case ErrorType.NETWORK:
        return 'error';
      case ErrorType.NOT_FOUND:
        return 'info';
      default:
        return 'error';
    }
  }

  /**
   * จัดการ Error และแสดงให้ผู้ใช้เห็นในขั้นตอนเดียว
   */
  static handleAndShowError(error: any, context?: string): FormattedError {
    const formattedError = this.handleError(error, context);
    this.showError(formattedError);
    return formattedError;
  }

  /**
   * ตรวจสอบว่า Error สามารถลองใหม่ได้หรือไม่
   */
  static canRetry(error: any): boolean {
    const formattedError = this.formatError(error);
    return formattedError.canRetry;
  }
}

export default ErrorHandler;